import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { LogIn, Menu, X, Sun, Moon, Palette } from "lucide-react";
import { useTheme } from "../Utils/useTheme";

export default function Navbar({ theme: navTheme }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { theme, setTheme } = useTheme();
  const location = useLocation();
  const currentPath = location.pathname;

  const isActive = (path) => {
    if (path === "/features") {
      return currentPath.startsWith("/features");
    }
    return currentPath === path;
  };

  const getLinkClass = (path) => {
    const active = isActive(path);
    if (active) {
      return "text-orange-500 font-semibold drop-shadow-[0_0_8px_var(--border-color)] transition-all";
    }
    return `transition-all hover:text-orange-500 hover:drop-shadow-[0_0_10px_rgba(249,115,22,0.4)] ${
      isDark ? "text-slate-300" : "text-slate-600"
    }`;
  };

  const getMobileLinkClass = (path) => {
    const active = isActive(path);
    if (active) {
      return "py-2 transition-colors text-orange-500 font-bold drop-shadow-[0_0_6px_var(--border-color)]";
    }
    return `py-2 transition-colors hover:text-orange-500 ${
      isDark ? "text-slate-300" : "text-slate-600"
    }`;
  };

  // The navigation design automatically adapts: isNavy (dark navy style) when dark theme is active.
  const isDark = theme !== "light";

  const getThemeBtnClass = (btnTheme, isMobile = false) => {
    const isActive = theme === btnTheme;
    const basePadding = isMobile ? "px-3 py-1.5 text-xs font-semibold" : "p-1.5";
    const activeClass = btnTheme === "green"
      ? "bg-brand-orange text-white shadow-sm"
      : "bg-orange-500 text-white shadow-sm";
    
    if (isActive) {
      return `${basePadding} rounded-md transition-all cursor-pointer ${activeClass}`;
    }
    
    const inactiveClass = isDark
      ? "text-slate-400 hover:text-slate-200 hover:bg-slate-800/30"
      : "text-slate-500 hover:text-slate-800 hover:bg-slate-200";
      
    return `${basePadding} rounded-md transition-all cursor-pointer ${inactiveClass}`;
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <header
        className={`fixed top-0 left-0 w-full h-20 z-50 transition-all duration-300 ${isScrolled
            ? isDark
              ? "bg-[#0B1220]/90 backdrop-blur-md border-b border-orange-500/20 shadow-lg shadow-black/30"
              : "bg-white/90 backdrop-blur-md border-b border-orange-500/10 shadow-lg shadow-slate-100/30"
            : "bg-transparent border-b border-transparent"
          }`}
      >
        <div className="max-w-7xl mx-auto h-full px-6 flex items-center justify-between">

          {/* Logo */}
          <Link to="/" className="flex items-center select-none">
            <img src="/logo.png" alt="Logo" className="h-9 w-auto light-logo" />
            <img src="/logo-white.png" alt="Logo" className="h-9 w-auto dark-logo" />
          </Link>

          {/* Desktop Links (Center) */}
          <nav className="hidden md:flex items-center gap-8 font-title text-[14px] font-normal tracking-widest">
            <Link
              to="/features"
              className={getLinkClass("/features")}
            >
              Features
            </Link>
            <Link
              to="/about"
              className={getLinkClass("/about")}
            >
              About Us
            </Link>
            <Link
              to="/pricing"
              className={getLinkClass("/pricing")}
            >
              Pricing
            </Link>
            <Link
              to="/contact"
              className={getLinkClass("/contact")}
            >
              Contact
            </Link>
          </nav>

          {/* Desktop CTAs (Right) */}
          <div className="hidden md:flex items-center gap-4">
            {/* Theme Selector */}
            <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-950/40 p-1 rounded-lg border border-slate-200 dark:border-slate-800/60 mr-2">
              <button
                onClick={() => setTheme("light")}
                title="Light Theme"
                className={getThemeBtnClass("light")}
              >
                <Sun className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setTheme("dark")}
                title="Dark Theme"
                className={getThemeBtnClass("dark")}
              >
                <Moon className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setTheme("green")}
                title="Green Shadows Theme"
                className={getThemeBtnClass("green")}
              >
                <Palette className="w-3.5 h-3.5" />
              </button>
            </div>

            <Link
              to="https://admin.supplyspot.ai/auth/login"
              target="_blank"
              className={`inline-flex items-center gap-2 px-5 py-2.5 font-semibold text-xs tracking-wider rounded-lg bg-transparent transition-all duration-300 border ${isDark
                  ? "text-slate-200 border-orange-500/30 hover:border-amber-500 hover:text-amber-400 hover:bg-orange-500/5"
                  : "text-slate-700 border-slate-200 hover:border-orange-500 hover:text-orange-600 hover:bg-orange-500/5"
                }`}
            >
              <LogIn className="w-3.5 h-3.5" />
              <span>Sign In</span>
            </Link>
            <Link
              to="https://admin.supplyspot.ai/auth/signup" 
              target="_blank"
              className="px-5 py-2.5 font-semibold text-xs tracking-wider rounded-lg transition-all duration-300 text-white bg-gradient-to-r from-orange-500 to-amber-500 shadow-md shadow-orange-500/10 hover:shadow-orange-500/25 hover:scale-[1.02] active:scale-[0.98]"
            >
              Sign Up
            </Link>
          </div>

          {/* Mobile Toggle (Right) */}
          <div className="flex items-center gap-2 md:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`p-2 transition-colors ${isDark ? "text-slate-100 hover:text-amber-400" : "text-slate-600 hover:text-orange-500"
                }`}
              aria-label="Toggle navigation menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Drawer */}
      <div
        className={`fixed top-20 left-0 w-full z-40 md:hidden transition-all duration-300 overflow-hidden ${isMobileMenuOpen ? "max-h-[520px] opacity-100 py-6" : "max-h-0 opacity-0 py-0"
          } ${isDark
            ? "bg-[#0B1220]/95 backdrop-blur-lg border-b border-orange-500/20"
            : "bg-white/95 backdrop-blur-lg border-b border-orange-500/10 shadow-lg shadow-slate-100/10"
          }`}
      >
        <div className={`flex flex-col gap-5 px-6 font-title text-center text-[14px] font-normal tracking-widest ${isDark ? "text-slate-300" : "text-slate-600"
          }`}>
          <Link
            to="/features"
            onClick={() => setIsMobileMenuOpen(false)}
            className={getMobileLinkClass("/features")}
          >
            Features
          </Link>

          <Link
            to="/about"
            onClick={() => setIsMobileMenuOpen(false)}
            className={getMobileLinkClass("/about")}
          >
            About Us
          </Link>
          <Link
            to="/pricing"
            onClick={() => setIsMobileMenuOpen(false)}
            className={getMobileLinkClass("/pricing")}
          >
            Pricing
          </Link>
          <Link
            to="/contact"
            onClick={() => setIsMobileMenuOpen(false)}
            className={getMobileLinkClass("/contact")}
          >
            Contact
          </Link>

          <div className="flex flex-col gap-3 mt-4 pt-4 border-t border-slate-200 dark:border-slate-800">
            {/* Mobile Theme Selector */}
            <div className="flex items-center justify-between px-2 mb-3">
              <span className="text-[10px] font-bold tracking-widest text-text-secondary uppercase font-title">Theme</span>
              <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-950/40 p-1 rounded-lg border border-slate-200 dark:border-slate-800/60">
                <button
                  onClick={() => setTheme("light")}
                  className={getThemeBtnClass("light", true)}
                >
                  Light
                </button>
                <button
                  onClick={() => setTheme("dark")}
                  className={getThemeBtnClass("dark", true)}
                >
                  Dark
                </button>
                <button
                  onClick={() => setTheme("green")}
                  className={getThemeBtnClass("green", true)}
                >
                  Green
                </button>
              </div>
            </div>

            <Link
              to="https://admin.supplyspot.ai/auth/login"
              target="_blank"
              onClick={() => setIsMobileMenuOpen(false)}
              className={`inline-flex items-center justify-center gap-2 w-full py-3 rounded-lg text-sm transition-all border ${isDark
                  ? "text-slate-200 border-orange-500/30 hover:border-amber-500"
                  : "text-slate-700 border-slate-200 hover:border-orange-500"
                }`}
            >
              <LogIn className="w-4 h-4" />
              <span>Sign In</span>
            </Link>
            <Link
              to="https://admin.supplyspot.ai/auth/signup"
              target="_blank"
              onClick={() => setIsMobileMenuOpen(false)}
              className="w-full py-3 rounded-lg text-sm font-semibold text-center text-white bg-gradient-to-r from-orange-500 to-amber-500 shadow-md shadow-orange-500/10"
            >
              Sign Up
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
